/**
 * TrainingCostEstimator
 * ---------------------
 * Live, read-only cost preview that lives on the pipeline-creation
 * page right after the training schedule. Translates the user's
 * current configuration (blueprint OR foundation, segment, schedule,
 * intensity, lookback, horizon, blueprint count) into:
 *
 *   * cost per training run     (credits)
 *   * trainings per month       (derived from training_schedule)
 *   * total estimated monthly   (credits / month)
 *
 * Costs are sourced from the existing ``POST /v1/billing/estimate``
 * endpoint so plan-tier and per-user/org overrides are honoured. We
 * never expose USD - everything is rendered in credits, matching the
 * platform-wide credits convention (see PRICING.md).
 *
 * For "custom" blueprints (TF / PyTorch / sklearn / XGBoost), the
 * trainer is post-billed by elapsed CPU/GPU seconds, so we can only
 * estimate. The component derives a duration heuristic from the
 * blueprint's epochs/batch_size and the segment's training-row count
 * and clearly labels the result as approximate.
 *
 * Multi-model pools train (1 + extraModelCount) models per cycle —
 * every selected model; the monthly estimate is multiplied
 * accordingly (the per-run price of the extras is approximated by
 * the first selection's — exact runtimes differ per blueprint, so
 * the number stays labeled as approximate whenever more than one
 * model is selected).
 */

import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Stack, Chip } from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

import {
  fetchRequest as estimateRequest,
  clearState as estimateClear,
} from '../../redux/actions/billing/estimate';
import { formatCredits } from '../billing/billingHelpers';
import CostEstimateCard, {
  FactorRow, ExplainSectionTitle,
} from '../billing/CostEstimateCard';
import {
  estimateSegmentRows, computeSplitSizes,
} from '../../utils/dataSufficiency';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const FULL_DAY_SECONDS = 86400;
const WEEKS_PER_MONTH = 30 / 7; // ~4.286

// Heuristics for estimating wall-clock seconds of a "custom" training
// run on the CPU training-worker. Each framework has VERY different
// runtime characteristics, so we keep one constant block per backend
// and dispatch on `framework`. Numbers are conservative (slightly
// over-estimate real wall-clock) so a user never gets a bigger bill
// than the preview suggested.

// --- TF / PyTorch (mini-batch SGD) ---------------------------------
// Trainer iterates ``epochs × ceil(train_sequences / batch_size)``
// mini-batches; assume ~50ms per mini-batch on a small CPU LSTM and
// add a fixed startup overhead so tiny runs never round to zero.
const NN_SEC_PER_BATCH = 0.05;
const NN_STARTUP_SECONDS = 30;

// --- XGBoost (boosted trees, hist tree_method, CPU) ----------------
// Per tree: O(n_estimators × samples × max_depth) leaf-finding work
// using histogram bins. Empirically ~2ms per tree per 1k samples at
// max_depth=5 for single-output, scaling roughly linearly in depth.
// Native multi-output (XGBoost 2.x multi_strategy="multi_output_tree"
// — see core/src/app/models/frameworks/xgboost/builder.py) adds
// ~30-80% overhead per tree because each leaf carries an
// (H × L)-dim vector instead of a scalar. The legacy
// MultiOutputRegressor fallback is N× slower, but the builder uses
// native multi-output whenever XGBoost >= 2.0 (the install in
// requirements.txt is 2.0.3), so we price for the fast path.
const XGB_STARTUP_SECONDS = 15;
const XGB_SEC_PER_TREE_PER_KSAMPLE = 0.002;   // 2ms / tree / 1k samples at depth 5
const XGB_DEPTH_BASELINE = 5;
const XGB_MULTI_OUTPUT_BASE_OVERHEAD = 0.3;   // +30% baseline for vector leaves

// --- sklearn (per estimator family) --------------------------------
// sklearn estimators vary by 1-2 orders of magnitude in runtime, so
// we keep a small lookup keyed by class name with a conservative
// generic fallback for anything we haven't profiled yet.
const SKLEARN_STARTUP_SECONDS = 10;
const SKLEARN_RUNTIME_PER_KSAMPLE = {
  // Linear & generalized linear: closed-form / coordinate descent
  LinearRegression:        0.05,
  Ridge:                   0.05,
  Lasso:                   0.10,
  ElasticNet:              0.10,
  // Tree ensembles: scale with n_estimators (handled separately below)
  RandomForestRegressor:   0.0,    // base; + n_estimators * 0.02s / 1k samples
  GradientBoostingRegressor: 0.0,  // base; + n_estimators * 0.04s / 1k samples
  ExtraTreesRegressor:     0.0,    // base; + n_estimators * 0.015s / 1k samples
  // Single-tree
  DecisionTreeRegressor:   0.10,
  // Distance-based
  KNeighborsRegressor:     0.05,   // training is ~free; cost is at predict time
  // Slow estimators (quadratic in samples)
  SVR:                     2.0,    // O(n^2) — careful, blows up at scale
  // Neural-net-shaped
  MLPRegressor:            0.5,
  // Default for anything else
  __default__:             0.5,
};
const SKLEARN_PER_TREE_PER_KSAMPLE = {
  RandomForestRegressor:     0.020,
  GradientBoostingRegressor: 0.040,
  ExtraTreesRegressor:       0.015,
};

const CUSTOM_TRAIN_FALLBACK_SECONDS = 120;

const DEFAULT_TEST_SPLIT = 0.2;
const DEFAULT_VAL_SPLIT = 0.2;

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const timeToSeconds = (t) => {
  if (!t) return 0;
  const parts = String(t).split(':').map((x) => parseInt(x, 10) || 0);
  const [h = 0, m = 0, s = 0] = parts;
  return h * 3600 + m * 60 + s;
};

/**
 * Derive an approximate "trainings per month" from the schedule
 * shape used on the create page. The math works in three steps so it
 * stays correct across the four mode presets and arbitrary custom
 * cadences:
 *
 *   1. find the seconds-per-day window the scheduler is allowed to
 *      run in (`end - start`, treating 23:59:59 as 24h);
 *   2. compute runs per week = days_per_week × window / frequency;
 *   3. multiply by the average weeks/month (~4.286).
 *
 * Returns ``null`` when the schedule is too sparse to interpret
 * (e.g. zero days selected) - the caller renders a friendlier
 * fallback.
 */
function trainingsPerMonth(schedule) {
  if (!schedule) return null;
  const freq = parseInt(schedule.frequency, 10);
  if (!Number.isFinite(freq) || freq <= 0) return null;

  const days = Array.isArray(schedule.days) ? schedule.days.length : 7;
  if (days === 0) return 0;

  const startSec = timeToSeconds(schedule.start_time || '00:00:00');
  const endSec = timeToSeconds(schedule.end_time || '23:59:59');
  // 23:59:59 is the canonical "rest of the day" sentinel. Round it up
  // to 24h so a daily schedule yields exactly 1 run/day, not 0.999...
  const rawWindow = endSec >= FULL_DAY_SECONDS - 1 && startSec === 0
    ? FULL_DAY_SECONDS
    : Math.max(60, endSec - startSec);

  const runsPerWeek = (days * rawWindow) / freq;
  return Math.max(0, runsPerWeek * WEEKS_PER_MONTH);
}

/**
 * Friendly summary of the schedule. Mirrors the wording the
 * TrainingScheduleField uses so the estimator and schedule editor
 * agree at a glance.
 */
function describeSchedule(schedule) {
  if (!schedule) return 'No schedule set';
  const freq = parseInt(schedule.frequency, 10);
  const days = Array.isArray(schedule.days) ? schedule.days : [];
  if (days.length === 0) return 'Run once — then manual / triggered runs only';
  if (!Number.isFinite(freq) || freq <= 0) return 'Invalid frequency';

  const all7 = days.length === 7;
  const startSec = timeToSeconds(schedule.start_time || '00:00:00');
  const endSec = timeToSeconds(schedule.end_time || '23:59:59');
  const fullDay = startSec === 0 && endSec >= FULL_DAY_SECONDS - 1;

  if (all7 && fullDay && freq === 3600) return 'Hourly, every day';
  if (all7 && fullDay && freq === FULL_DAY_SECONDS) return 'Daily, every day';
  if (days.length === 1 && fullDay && freq === FULL_DAY_SECONDS) {
    return `Weekly, on ${days[0]}`;
  }

  // Custom: report the cadence in the largest clean unit.
  let n;
  let unit;
  if (freq % FULL_DAY_SECONDS === 0) { n = freq / FULL_DAY_SECONDS; unit = 'day'; }
  else if (freq % 3600 === 0)        { n = freq / 3600;            unit = 'hour'; }
  else                                { n = Math.round(freq / 60);  unit = 'minute'; }
  const unitLbl = n === 1 ? unit : `${unit}s`;
  const dayLbl = all7
    ? 'every day'
    : days.length <= 3
      ? `on ${days.map((d) => d.slice(0, 3)).join(', ')}`
      : `${days.length} days/wk`;
  const windowLbl = fullDay
    ? ''
    : `, ${(schedule.start_time || '').slice(0, 5)}–${(schedule.end_time || '').slice(0, 5)}`;
  return `Every ${n} ${unitLbl}, ${dayLbl}${windowLbl}`;
}

/**
 * Resolve the ``(operation, units, label, intensity?)`` tuple that
 * matches what the trainer will actually charge. Mirrors the
 * resolution in ``api/model/management.py::_resolve_upfront_training_cost``
 * + the custom-training charge in ``app/models/model.py``.
 *
 * Returns ``null`` when we don't have enough info yet (e.g. blueprint
 * not picked or foundation backbone still loading). The caller renders
 * an empty-state instead of a number.
 */
function resolveOperation({
  isFoundationMode, foundationFamily, formIntensity,
  blueprint, segment,
}) {
  // Foundation fine-tuning is a flat, family-priced charge. The
  // predictfm family bills through ``adapter_training.*``; every
  // other family through ``external_fm_training.<family>.*``.
  const foundationOp = (family, intensity) => (
    family === 'predictfm'
      ? {
        kind: 'flat',
        operation: `adapter_training.${intensity}`,
        units: 1,
        label: 'Foundation fine-tune (predictfm)',
        intensity,
        family,
      }
      : {
        kind: 'flat',
        operation: `external_fm_training.${family}.${intensity}`,
        units: 1,
        label: `Foundation fine-tune (${family.replace(/_/g, ' ')})`,
        intensity,
        family,
      }
  );

  if (isFoundationMode) {
    const intensity = (formIntensity || 'adapt').toLowerCase();
    if (!foundationFamily) return null;
    return foundationOp(foundationFamily, intensity);
  }

  if (!blueprint) return null;
  const bpKind = blueprint.kind || 'custom';

  if (bpKind === 'foundation') {
    const recipe = blueprint.foundation || {};
    const family = recipe.family || 'chronos_bolt';
    const intensity = (recipe.intensity || 'adapt').toLowerCase();
    return foundationOp(family, intensity);
  }

  if (bpKind === 'custom') {
    // Post-billed by elapsed seconds. Use a framework-aware runtime
    // estimator since each backend's training characteristics are
    // very different (NN: epochs × batches; XGBoost: n_estimators ×
    // samples × depth; sklearn: depends on estimator type).
    const framework = _detectFramework(blueprint);
    const seqLen = parseInt(blueprint?.training_details?.sequence_length, 10) || 1;
    const totalRows = estimateSegmentRows(segment) || 0;
    const splits = computeSplitSizes(totalRows, DEFAULT_TEST_SPLIT, DEFAULT_VAL_SPLIT);
    const trainSeqs = Math.max(1, splits.trainRows - seqLen);

    const { seconds, breakdown } = _estimateRuntimeSeconds({
      framework, blueprint, segment, trainSeqs,
    });

    return {
      kind: 'duration',
      operation: 'custom_training.cpu_per_second',
      units: Math.max(1, Math.round(seconds)),
      label: `Custom training — ${framework} (CPU)`,
      approximate: true,
      framework,
      breakdown,
    };
  }

  return null;
}

// -----------------------------------------------------------------------------
// Framework detection + per-framework runtime heuristics
// -----------------------------------------------------------------------------

// Read the framework off a blueprint, normalising the various places it
// can live for backwards compatibility (top-level `framework`, nested
// metadata, or implied by `model_blueprint.estimator`).
function _detectFramework(blueprint) {
  const direct = (blueprint?.framework
    || blueprint?.metadata?.framework
    || blueprint?.model_blueprint?.metadata?.framework
    || ''
  ).toString().toLowerCase();
  if (direct.includes('pytorch') || direct === 'torch') return 'pytorch';
  if (direct.includes('xgboost') || direct === 'xgb')   return 'xgboost';
  if (direct.includes('sklearn') || direct === 'scikit-learn') return 'sklearn';
  if (direct.includes('tensorflow') || direct === 'tf' || direct === 'keras') return 'tensorflow';
  // Estimator-shaped blueprint without a framework tag → sklearn.
  if (blueprint?.model_blueprint?.estimator) return 'sklearn';
  // hyperparameters with n_estimators + tree_method → xgboost.
  const hp = blueprint?.model_blueprint?.hyperparameters || {};
  if (hp.tree_method || hp.booster) return 'xgboost';
  return 'tensorflow';
}

// Returns ``{ seconds, breakdown }`` where breakdown is a list of
// ``{ label, value }`` rows the UI surfaces under "Cost factors".
function _estimateRuntimeSeconds({ framework, blueprint, segment: _seg, trainSeqs }) {
  if (framework === 'tensorflow' || framework === 'pytorch') {
    return _estimateNN({ blueprint, trainSeqs });
  }
  if (framework === 'xgboost') {
    return _estimateXGBoost({ blueprint, trainSeqs });
  }
  if (framework === 'sklearn') {
    return _estimateSklearn({ blueprint, trainSeqs });
  }
  return {
    seconds: CUSTOM_TRAIN_FALLBACK_SECONDS,
    breakdown: [{ label: 'Heuristic', value: 'Generic fallback (~120s)' }],
  };
}

function _estimateNN({ blueprint, trainSeqs }) {
  const epochs = parseInt(blueprint?.training_details?.epochs, 10);
  const batchSize = parseInt(blueprint?.training_details?.batch_size, 10);
  if (!Number.isFinite(epochs) || epochs <= 0 || !Number.isFinite(batchSize) || batchSize <= 0) {
    return {
      seconds: CUSTOM_TRAIN_FALLBACK_SECONDS,
      breakdown: [{ label: 'Heuristic', value: 'Default fallback (~120s)' }],
    };
  }
  const batches = Math.max(1, Math.ceil(trainSeqs / batchSize));
  const seconds = NN_STARTUP_SECONDS + epochs * batches * NN_SEC_PER_BATCH;
  return {
    seconds,
    breakdown: [
      { label: 'Train sequences', value: trainSeqs.toLocaleString() },
      { label: 'Mini-batches / epoch', value: batches.toLocaleString() },
      { label: 'Epochs', value: epochs.toLocaleString() },
      { label: 'Per-batch (CPU)', value: `${(NN_SEC_PER_BATCH * 1000).toFixed(0)}ms` },
    ],
  };
}

function _estimateXGBoost({ blueprint, trainSeqs }) {
  const hp = blueprint?.model_blueprint?.hyperparameters || {};
  const nEstimators = parseInt(hp.n_estimators, 10) || 200;
  const maxDepth = parseInt(hp.max_depth, 10) || 6;
  const seqLen = parseInt(blueprint?.training_details?.sequence_length, 10) || 1;
  const horizon = parseInt(blueprint?.forecast_horizon, 10)
    || parseInt(blueprint?.training_details?.forecast_horizon, 10)
    || 1;

  // Effective sample count for the XGB fit is the number of (window,
  // horizon) pairs after windowing. Samples drive the histogram
  // build cost; depth drives the per-tree split-finding cost; outputs
  // drive the per-leaf vector update cost.
  const samples = Math.max(1, trainSeqs);
  const depthFactor = Math.max(0.5, maxDepth / XGB_DEPTH_BASELINE);
  const nOutputs = Math.max(1, horizon);
  // Vector-leaf overhead grows sub-linearly with output dim
  // (XGBoost reuses the same split structure across outputs); we
  // model this as 30% baseline + ~5% per doubling of outputs above 1.
  const outputFactor = nOutputs <= 1
    ? 1.0
    : 1.0 + XGB_MULTI_OUTPUT_BASE_OVERHEAD + 0.05 * Math.log2(nOutputs);

  const treeSeconds = nEstimators
    * (samples / 1000)
    * depthFactor
    * outputFactor
    * XGB_SEC_PER_TREE_PER_KSAMPLE;
  const seconds = XGB_STARTUP_SECONDS + treeSeconds;

  return {
    seconds,
    breakdown: [
      { label: 'Train windows', value: samples.toLocaleString() },
      { label: 'Boosting rounds', value: nEstimators.toLocaleString() },
      { label: 'Max depth', value: String(maxDepth) },
      { label: 'Outputs / leaf',
        value: nOutputs > 1 ? `${nOutputs} (vector leaves)` : '1 (scalar)' },
      { label: 'Sequence length',
        value: seqLen > 1 ? String(seqLen) : '1 (no windowing)' },
    ],
  };
}

function _estimateSklearn({ blueprint, trainSeqs }) {
  const estimator = blueprint?.model_blueprint?.estimator || '__default__';
  const hp = blueprint?.model_blueprint?.hyperparameters || {};
  const nEstimators = parseInt(hp.n_estimators, 10) || 100;
  const samples = Math.max(1, trainSeqs);
  const baseSec = SKLEARN_RUNTIME_PER_KSAMPLE[estimator]
    ?? SKLEARN_RUNTIME_PER_KSAMPLE.__default__;
  const perTreeSec = SKLEARN_PER_TREE_PER_KSAMPLE[estimator] || 0;
  const isEnsemble = perTreeSec > 0;
  const samplesK = samples / 1000;

  const fitSec = baseSec * samplesK
    + (isEnsemble ? perTreeSec * nEstimators * samplesK : 0);
  const seconds = SKLEARN_STARTUP_SECONDS + fitSec;

  const breakdown = [
    { label: 'Estimator', value: estimator },
    { label: 'Train windows', value: samples.toLocaleString() },
  ];
  if (isEnsemble) {
    breakdown.push({ label: 'n_estimators', value: nEstimators.toLocaleString() });
  }
  return { seconds, breakdown };
}

// Pretty intensity chip color
const intensityTone = (intensity) => {
  if (intensity === 'forge') return 'error';
  if (intensity === 'specialize') return 'warning';
  return 'info';
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const TrainingCostEstimator = ({
  blueprint,
  segment,
  isFoundationMode,
  foundationFamily,
  trainingIntensity,
  lookback,
  forecastHorizon,
  extraModelCount = 0,
  trainingSchedule,
  balance, // optional: { current_balance, ... } from billing_balance redux slice
}) => {
  // Resolve the operation + units we need to price.
  const op = useMemo(
    () => resolveOperation({
      isFoundationMode,
      foundationFamily,
      formIntensity: trainingIntensity,
      blueprint,
      segment,
    }),
    [isFoundationMode, foundationFamily,
     trainingIntensity, blueprint, segment],
  );

  const dispatch = useDispatch();
  const estimateState = useSelector((s) => s.billing_estimate);

  // Fetch the per-unit credit cost from /billing/estimate. We always
  // pass units=1 here and multiply on the client so that the same
  // response is reusable across cadence/duration changes without a
  // round trip.
  //
  // The saga's ``takeLatest`` protects against StrictMode double-mount
  // and rapid op changes - the latest pending request always wins.
  useEffect(() => {
    if (!op?.operation) {
      dispatch(estimateClear());
      return;
    }
    dispatch(estimateRequest({ operation: op.operation, units: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op?.operation, dispatch]);

  // Don't leak this page's estimate into other billing_estimate consumers.
  useEffect(() => () => { dispatch(estimateClear()); }, [dispatch]);

  const estimateLoading = Boolean(estimateState?.isLoading);
  const estimateError = estimateState?.error || null;
  const perUnitCost = useMemo(() => {
    const payload = estimateState?.response;
    if (payload == null) return null;
    const cpu = Number(payload.cost_per_unit ?? payload.cost_credits ?? 0);
    return Number.isFinite(cpu) ? cpu : 0;
  }, [estimateState?.response]);

  // Derived numbers ---------------------------------------------------------
  const perTrainingCost = useMemo(() => {
    if (perUnitCost == null || !op) return null;
    return Math.max(0, perUnitCost * (op.units || 1));
  }, [perUnitCost, op]);

  const monthlyRuns = useMemo(
    () => trainingsPerMonth(trainingSchedule),
    [trainingSchedule],
  );

  // Each cycle trains every selected model, so the monthly projection
  // scales by the model count. Per-run cost stays first-model-only —
  // that's what a single training bills.
  const runsMultiplier = 1 + Math.max(0, Number(extraModelCount) || 0);

  const monthlyCost = useMemo(() => {
    if (perTrainingCost == null || monthlyRuns == null) return null;
    return perTrainingCost * monthlyRuns * runsMultiplier;
  }, [perTrainingCost, monthlyRuns, runsMultiplier]);

  const balanceCredits = balance?.current_balance ?? null;

  // Coverage analysis. Returns one of three tiers so the footer can
  // render the right tone + CTA:
  //   * "insufficient" - balance can't pay for a single training run.
  //                      Block the user with a warning + Top up CTA.
  //   * "partial"      - balance covers some trainings but not a full
  //                      month at this cadence. Show training count
  //                      and a soft warning.
  //   * "ok"           - balance covers >= 1 month. Show months covered.
  // Returns ``null`` when we don't have enough data to compute (e.g.
  // balance hasn't loaded, cost still pending) - the caller renders
  // a balance-only line in that case.
  const coverage = useMemo(() => {
    if (!Number.isFinite(balanceCredits) || balanceCredits == null) return null;
    if (perTrainingCost == null || perTrainingCost <= 0) return null;

    const trainingsCovered = balanceCredits / perTrainingCost;
    const shortfallForOne = Math.max(0, perTrainingCost - balanceCredits);

    if (balanceCredits < perTrainingCost) {
      return {
        tier: 'insufficient',
        trainingsCovered,
        shortfallForOne,
      };
    }

    if (monthlyCost == null || monthlyCost <= 0) {
      return { tier: 'ok', monthsCovered: null, trainingsCovered };
    }

    const monthsCovered = balanceCredits / monthlyCost;
    if (monthsCovered < 1) {
      return { tier: 'partial', monthsCovered, trainingsCovered };
    }
    return { tier: 'ok', monthsCovered, trainingsCovered };
  }, [balanceCredits, perTrainingCost, monthlyCost]);

  // ------------------------------------------------------------------------
  // Render via the shared CostEstimateCard so this estimator looks
  // identical to the Goals estimate (and every other cost preview).
  // ------------------------------------------------------------------------
  const hasEnoughInputs = !!op;

  const stats = hasEnoughInputs ? [
    {
      label: 'Per training',
      value: perTrainingCost,
      loading: estimateLoading,
      error: estimateError,
      suffix: op.approximate ? '~' : '',
    },
    {
      label: 'Trainings / month',
      value: monthlyRuns != null ? monthlyRuns * runsMultiplier : monthlyRuns,
      raw: true,
    },
    {
      label: 'Estimated / month',
      value: monthlyCost,
      loading: estimateLoading,
      error: estimateError,
      accent: true,
      suffix: (op.approximate || runsMultiplier > 1) ? '~' : '',
    },
  ] : null;

  const billingModes = hasEnoughInputs
    ? [
      'per_run',
      ...(op.kind === 'duration' ? ['usage_based'] : []),
      ...(monthlyRuns > 0 ? ['monthly_recurring'] : []),
    ]
    : [];

  // Map the internal coverage analysis onto the shared footer contract.
  const coverageProps = (() => {
    if (!coverage) return null;
    if (coverage.tier === 'insufficient') {
      return {
        tier: 'insufficient',
        detail: `Your balance of ${formatCredits(balanceCredits)} credits won't cover a `
          + `single training run (${formatCredits(perTrainingCost)} credits). Add at `
          + `least ${formatCredits(coverage.shortfallForOne)} more credits to start training.`,
      };
    }
    if (coverage.tier === 'partial') {
      return {
        tier: 'partial',
        chipLabel: `≈ covers ${Math.floor(coverage.trainingsCovered)} of ~${Math.round((monthlyRuns || 0) * runsMultiplier)} monthly trainings`,
      };
    }
    return {
      tier: 'ok',
      chipLabel: coverage.monthsCovered != null
        ? `≈ covers ${coverage.monthsCovered.toFixed(1)} month${coverage.monthsCovered >= 1.5 ? 's' : ''}`
        : null,
    };
  })();

  const explainContent = hasEnoughInputs ? (
    <>
      <ExplainSectionTitle sx={{ display: 'block', mb: 1 }}>Cost factors</ExplainSectionTitle>
      <Stack spacing={0.75}>
        <FactorRow
          label="Training kind"
          value={op.label}
          affects
          hint={op.kind === 'duration'
            ? 'Custom training is billed per second of compute. Shown as an estimate; final cost depends on actual elapsed time.'
            : 'Foundation tunings are billed at a flat per-run rate set by intensity.'}
        />
        {op.intensity && (
          <FactorRow
            label="Training intensity"
            value={(
              <Chip
                size="small"
                color={intensityTone(op.intensity)}
                label={op.intensity}
                sx={{ height: 20, textTransform: 'capitalize' }}
              />
            )}
            affects
            hint="adapt = small LoRA (cheapest); specialize = larger LoRA + heads; forge = full fine-tune (most expensive)."
          />
        )}
        {op.kind === 'duration' && (
          <FactorRow
            label="Estimated runtime"
            value={
              op.units >= 60
                ? `~${Math.round(op.units / 60)} min / training`
                : `~${op.units.toLocaleString()} sec / training`
            }
            affects
            hint={
              op.framework === 'tensorflow' || op.framework === 'pytorch'
                ? 'Estimated from epochs × ceil(train_sequences ÷ batch_size) × ~50ms/batch + 30s startup. Real runs vary based on model complexity and CPU.'
                : op.framework === 'xgboost'
                  ? 'Estimated from n_estimators × samples × max_depth × output_dim. Native multi-output (XGBoost 2.x) is fast — single booster with vector leaves.'
                  : op.framework === 'sklearn'
                    ? 'Estimated from estimator type, sample count, and (for ensembles) n_estimators. Real runtime varies widely between estimator families.'
                    : 'Heuristic estimate. Real runtime varies based on model and data.'
            }
          />
        )}
        {op.kind === 'duration' && Array.isArray(op.breakdown) && op.breakdown.length > 0 && (
          <FactorRow
            label="Runtime breakdown"
            value={op.breakdown.map((b) => `${b.label}: ${b.value}`).join('  ·  ')}
            affects
            hint="The factors that determine the runtime estimate above. Edit your model to change any of these."
          />
        )}
        <FactorRow
          label="Schedule"
          value={describeSchedule(trainingSchedule)}
          affects
          hint="Drives how many trainings happen per month. Edit the schedule above to change cadence."
        />
        {Number.isFinite(Number(lookback)) && Number(lookback) > 0 && (
          <FactorRow
            label="Lookback"
            value={Number(lookback).toLocaleString()}
            affects={op.kind === 'duration'}
            hint={op.kind === 'duration'
              ? 'Affects how many windows fit in the segment - more sequences mean longer training.'
              : 'Set on the foundation model; does not change the flat per-run price.'}
          />
        )}
        <FactorRow
          label="Forecast horizon"
          value={Number(forecastHorizon) || '—'}
          affects={false}
          hint="Horizon shapes the window math but does not change the credit cost of a training run."
        />
        {runsMultiplier > 1 && (
          <FactorRow
            label="Models"
            value={(
              <Chip
                size="small"
                icon={<AutoAwesomeRoundedIcon sx={{ fontSize: 14 }} />}
                label={`${runsMultiplier} models — ${runsMultiplier}× runs`}
                color="secondary"
                sx={{ height: 20 }}
              />
            )}
            affects
            hint="Every run trains all selected models on the same data, so monthly spend scales by the model count. Extra runs are priced at the primary model's per-run cost."
          />
        )}
      </Stack>
    </>
  ) : null;

  return (
    <CostEstimateCard
      sx={{ mt: 2 }}
      title="Cost estimator"
      infoTip="Estimates the credit cost of this pipeline based on your plan, the chosen training kind, and the training schedule. Uses live pricing from your account so per-tier and per-user discounts are reflected."
      billingModes={billingModes}
      stats={stats}
      emptyText={isFoundationMode
        ? 'Pick a foundation model to see the estimated cost.'
        : 'Pick a model to see the estimated cost.'}
      balance={balanceCredits}
      coverage={coverageProps}
      explainTitle="Cost breakdown"
      explainContent={explainContent}
      explainFootnote="All amounts are in credits. Storage and inference charges (which depend on traffic and retention) are billed separately."
    />
  );
};

export default TrainingCostEstimator;
